import React from 'react';
import styled from 'styled-components';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack';
import { Body, SecondaryBody } from '@grnhse/seedling/lib/birch/typography';

import { TextAreaField, DatePickerField } from '../../shared/form/Fields';
import Tokens from '@grnhse/seedling/lib/birch/tokens';
import { RejectModalOptionsResponse } from '../../shared/types';
import RejectionReasonFields from './rejection_reason_fields';
import RejectionEmailFields from './rejection_email_fields';

import { withLocalePrefix } from '../../../shared/utils/translation';
import Container from '@grnhse/seedling/lib/birch/components/container';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import { SectionTitle } from '@grnhse/seedling/lib/birch/typography';
import { Grid } from '@grnhse/seedling/lib/birch/layouts/grid';
import RadioGroup from '@grnhse/seedling/lib/birch/components/radio_group';
import { CompactRow } from '@grnhse/seedling/lib/birch/layouts/compact_row';
import CheckboxField from '@grnhse/seedling/lib/birch/components/forms/fields/checkbox_field';
import { InformationalCallOut } from '@grnhse/seedling/lib/birch/components/call_out';
import {
  Controller,
  useFormContext,
} from '@grnhse/seedling/lib/birch/components/forms/form';
import ShieldExclamationIcon from '@grnhse/seedling/lib/birch/icons/shield_exclamation';

const t = withLocalePrefix('candidate_profile_redesign.modals');

export type RejectionCategory = 'default' | 'security_risk';

type RadioGroupFieldProps = {
  name: string;
};

const RadioGroupField = ({ name }: RadioGroupFieldProps) => {
  const radioOptions: RejectionCategory[] = ['default', 'security_risk'];
  const { reset, getValues, control } = useFormContext();

  const RadioWrapper = styled(Container)<{ selected: boolean }>`
    border-radius: 8px;
    padding: ${Tokens.spacing['2']};
    flex: 1;

    fieldset {
      padding: 0;
      margin: 0;
    }

    ${({ selected }) =>
      selected &&
      `
      border-color: ${FullPalette.GREEN_700};
      box-shadow: 0 2px 3px rgba(0, 0, 0, 0.2);
    `}
  `;

  const handleChange = (
    newValue: RejectionCategory,
    fieldOnChange: (value: RejectionCategory) => void
  ) => {
    const currentNote = getValues('rejection_note');
    reset({
      rejection_category: newValue,
      rejection_reason: null,
      custom_rejection_type: null,
      custom_rejection_reason: null,
      rejection_note: currentNote,
    });
    fieldOnChange(newValue);
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const currentValue = field.value || 'default';

        return (
          <Grid container>
            {radioOptions.map((option) => {
              // HACK, the right way to do this would be store the first value in a ref and use that to set autoFocus
              // but then we'll have conditional hooks, it only uses the first render value and you can't
              // pass data-auto-focus=false or it doesn't work
              const autoFocus: {
                'data-autofocus-inside'?: boolean;
              } = {};
              if (currentValue === option) {
                autoFocus['data-autofocus-inside'] = true;
              }
              return (
                <Grid
                  item
                  xs={12}
                  md={6}
                  key={option}
                  data-selected={currentValue === option}
                  data-provides={`reject-reason-category-${option}-wrapper`}
                  container
                  spacing={0}
                  {...autoFocus}
                >
                  <RadioWrapper selected={currentValue === option}>
                    <RadioGroup
                      data-provides={`reject-reason-category-${option}`}
                      ariaLabel={t(`reject.rejection_category.${option}.label`)}
                      data-selected={currentValue === option}
                      options={[
                        {
                          label: (
                            <Stack spacing={1}>
                              <CompactRow
                                spacing={1}
                                alignItems="flex-start"
                                sx={{
                                  marginBottom:
                                    option === 'security_risk'
                                      ? '-12px'
                                      : '-8px',
                                }}
                              >
                                <Body medium>
                                  {t(
                                    `reject.rejection_category.${option}.label`
                                  )}
                                </Body>
                                {option === 'security_risk' && (
                                  <ShieldExclamationIcon
                                    size={'sm'}
                                    color={FullPalette.POPPY_500}
                                  />
                                )}
                              </CompactRow>
                              <SecondaryBody
                                style={{ color: FullPalette.EVERGREEN_600 }}
                              >
                                {t(
                                  `reject.rejection_category.${option}.helper_text`
                                )}
                              </SecondaryBody>
                            </Stack>
                          ),
                          value: option,
                        },
                      ]}
                      value={currentValue}
                      onChange={(value) =>
                        handleChange(value as RejectionCategory, field.onChange)
                      }
                    />
                  </RadioWrapper>
                </Grid>
              );
            })}
          </Grid>
        );
      }}
    />
  );
};

const RejectFormBody = ({
  rejectionReasons,
  referredByAgency,
  otherApplications,
  editing = false,
  canCreateProspect,
  canUseSecurityRiskRejection = false,
  email,
  applicationsToRejectInBulkCount,
  bulkMode,
  dateFormatBirchPicker,
  cannotEmailReason,
  getCcOptionsPath,
  shouldDisplayEmailDomainWarning,
  stageTypes,
  userOptionsPath,
  canSendEmail,
}: RejectModalOptionsResponse & {
  bulkMode: boolean;
  applicationsToRejectInBulkCount?: number;
  cannotEmailReason?: string | null;
  canSendEmail?: boolean;
}) => {
  const { watch } = useFormContext();
  const rejectionCategory = watch('rejection_category');
  const isSecurityRisk = rejectionCategory === 'security_risk';

  const filteredRejectionOptions = React.useMemo(() => {
    const options = rejectionReasons.rejectionOptions;

    if (!canUseSecurityRiskRejection) {
      return options;
    }

    return options.filter((group) => {
      if (isSecurityRisk) {
        return group.value === 'SECURITY CONCERN';
      } else {
        return group.value !== 'SECURITY CONCERN';
      }
    });
  }, [
    rejectionReasons.rejectionOptions,
    isSecurityRisk,
    canUseSecurityRiskRejection,
  ]);

  return (
    <>
      <Stack>
        {canUseSecurityRiskRejection && (
          <Stack spacing={2}>
            <SectionTitle>{t('reject.details')}</SectionTitle>
            <RadioGroupField name="rejection_category" />
          </Stack>
        )}
        {editing && (
          <DatePickerField
            fullWidth
            name="rejected_at"
            label={t('reject.date')}
            dateFormat={dateFormatBirchPicker}
          />
        )}
        {bulkMode && (
          <InformationalCallOut bodyText={t('reject.bulk.warning')} />
        )}
        <RejectionReasonFields
          {...rejectionReasons}
          rejectionOptions={filteredRejectionOptions}
          stageTypes={stageTypes}
          dateFormatBirchPicker={dateFormatBirchPicker}
          userOptionsPath={userOptionsPath}
          hideCreateNewReason={isSecurityRisk}
        />
        <SectionTitle>{t('reject.options')}</SectionTitle>
        <TextAreaField
          compact
          name="rejection_note"
          label={t('reject.notes')}
          helperText={bulkMode ? t('reject.bulk.notes') : undefined}
        />
        {!isSecurityRisk && referredByAgency && (
          <TextAreaField
            compact
            name="notes_to_agency"
            label={t('reject.notes_to_agency')}
          />
        )}
        {!bulkMode && otherApplications?.canRejectFromAll && !editing && (
          <CheckboxField
            label={t('reject.reject_from_all', {
              count: otherApplications.numberOfOtherHiringPlans + 1,
            })}
            name="reject_from_all"
            value="reject_from_all"
            helperText={
              !isSecurityRisk ? t('reject.reject_all_warning') : undefined
            }
          />
        )}
        {isSecurityRisk && !editing && (
          <CheckboxField
            label={t('reject.mark_as_do_not_email')}
            name="mark_as_do_not_email"
            value="mark_as_do_not_email"
          />
        )}
        {!isSecurityRisk && !bulkMode && canCreateProspect && !editing && (
          <CheckboxField
            label={t('reject.start_new_prospect')}
            name="start_new_prospect_process"
            value="start_new_prospect_process"
          />
        )}

        {!isSecurityRisk && !editing && (
          <Stack>
            <SectionTitle>{t('reject.rejection_email')}</SectionTitle>
            {canSendEmail ? (
              <RejectionEmailFields
                {...email}
                bulkMode={bulkMode}
                canSendEmail={canSendEmail}
                numOfCandidatesToReject={applicationsToRejectInBulkCount || 0}
                getCcOptionsPath={getCcOptionsPath}
                shouldDisplayEmailDomainWarning={
                  shouldDisplayEmailDomainWarning
                }
                dateFormatBirchPicker={dateFormatBirchPicker}
              />
            ) : (
              <Body>{cannotEmailReason}</Body>
            )}
          </Stack>
        )}
      </Stack>
    </>
  );
};

export default RejectFormBody;
